// src/components/ui/PermissionHelpModal.jsx
//
// Modal de PERMISSÃO NEGADA (v3.7.3): quando câmera/microfone/fotos estão
// bloqueados, mostra o passo a passo ESPECÍFICO da plataforma (iOS/Android/
// navegador) + botão que abre direto a tela de configurações do app.
// Antes cada tela só dizia "tente novamente" — inútil quando o sistema já não
// pergunta mais.
//
// Portal em document.body com z-index acima das câmeras full-screen (que
// também são portais) — funciona por cima do CustomCamera, QRScanner e chat.

import React from "react";
import { createPortal } from "react-dom";
import Style from "./PermissionHelpModal.module.css";
import {
  plataformaApp,
  abrirConfiguracoesApp,
} from "../../utils/permissoesApp";

const NOMES = {
  camera: {
    titulo: "Permita o acesso à câmera",
    recurso: "a câmera",
    ios: "Câmera",
    android: "Câmera",
  },
  microfone: {
    titulo: "Permita o acesso ao microfone",
    recurso: "o microfone",
    ios: "Microfone",
    android: "Microfone",
  },
  camera_microfone: {
    titulo: "Permita câmera e microfone",
    recurso: "a câmera e o microfone",
    ios: "Câmera e Microfone",
    android: "Câmera e Microfone",
  },
  fotos: {
    titulo: "Permita o acesso às fotos",
    recurso: "suas fotos",
    ios: "Fotos",
    android: "Fotos e vídeos",
  },
};

function passosPara(tipo, plataforma) {
  const nome = NOMES[tipo] || NOMES.camera;
  if (plataforma === "ios") {
    return [
      <>Toque em <strong>Abrir configurações do app</strong> abaixo — o iPhone vai abrir os Ajustes já na página do LesVita.</>,
      <>Encontre <strong>{nome.ios}</strong> na lista de permissões.</>,
      <>Ative a chave (deixe verde){tipo === "fotos" ? " e escolha “Acesso Total”" : ""}.</>,
      <>Volte ao LesVita e toque em <strong>Tentar novamente</strong>.</>,
    ];
  }
  if (plataforma === "android") {
    return [
      <>Toque em <strong>Abrir configurações do app</strong> abaixo — o Android vai abrir as informações do LesVita.</>,
      <>Toque em <strong>Permissões</strong>.</>,
      <>Toque em <strong>{nome.android}</strong> e escolha <strong>Permitir</strong> (ou "Permitir apenas durante o uso").</>,
      <>Volte ao LesVita e toque em <strong>Tentar novamente</strong>.</>,
    ];
  }
  // web/PWA
  return [
    <>Clique no <strong>cadeado</strong> (ou no ícone de ajustes) na barra de endereço do navegador.</>,
    <>Encontre <strong>{nome.ios}</strong> e mude para <strong>Permitir</strong>.</>,
    <>Recarregue a página e toque em <strong>Tentar novamente</strong>.</>,
  ];
}

export default function PermissionHelpModal({
  show,
  tipo = "camera",
  onClose,
  onRetry,
}) {
  const plataforma = plataformaApp();
  const nome = NOMES[tipo] || NOMES.camera;
  const nativo = plataforma === "ios" || plataforma === "android";

  if (!show) return null;

  const conteudo = (
    <div className={Style.overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div className={Style.sheet} onClick={(e) => e.stopPropagation()}>
        <div className={Style.iconWrap} aria-hidden="true">
          {tipo === "microfone" ? "🎙️" : tipo === "fotos" ? "🖼️" : "📷"}
        </div>

        <h3 className={Style.title}>{nome.titulo}</h3>
        <p className={Style.body}>
          O LesVita não tem permissão para usar {nome.recurso} neste aparelho.
          Sem ela esta funcionalidade não abre — e o sistema não volta a
          perguntar sozinho. Leva menos de 30 segundos para liberar:
        </p>

        <ol className={Style.steps}>
          {passosPara(tipo, plataforma).map((passo, i) => (
            <li key={i} className={Style.step}>
              <span className={Style.stepNum}>{i + 1}</span>
              <span className={Style.stepText}>{passo}</span>
            </li>
          ))}
        </ol>

        {nativo && (
          <button
            type="button"
            className={Style.btnPrimary}
            onClick={() => abrirConfiguracoesApp()}
          >
            Abrir configurações do app
          </button>
        )}
        {onRetry && (
          <button
            type="button"
            className={nativo ? Style.btnSecondary : Style.btnPrimary}
            onClick={onRetry}
          >
            Tentar novamente
          </button>
        )}
        <button type="button" className={Style.btnGhost} onClick={onClose}>
          Agora não
        </button>
      </div>
    </div>
  );

  return createPortal(conteudo, document.body);
}
